import {ApplicationModalButton} from "../../../application/components/ApplicationModalButton";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {useShouldShowPrice} from "../../../hooks/use_should_show_price";
import {useLeadFormSimplifiedAbTest} from "../../../lead/hooks/use_lead_form_simplified_ab_test";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {getLeadButtonText} from "../../../lead/utils/get_lead_button_text";
import {getOfferDisplayStatus} from "../../../offer/utils/get_offer_display_status";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {getPropertyBadgeType} from "../../../property/utils/get_property_badge_type";
import {IPropertyConfiguratorProperty} from "../../../property_configurator/types/IPropertyConfiguratorProperty";
import {useAppSelector} from "../../../store/hooks";
import {gtmEventPropertyListClick} from "../../../tracking/google_tag_manager/gtm_event_property_list";
import {getCurrency} from "../../../utils/get_currency";
import {ViewType} from "../../../view_type/ViewType";
import {AskForPropertyButtonVariantBCD} from "./AskForPropertyButtonVariantBC";
import {PromotionButton} from "./PromotionButton";
import {ReservedButton} from "./ReservedButton";

interface IProps {
    property: IPropertyListApplicationButtonProperty;
    isAutoresponderEnabled: boolean;
    vendorId: number;
    applicationSourceSection?: ApplicationSourceSection;
    isPlansList?: boolean;
    className?: string;
    isInPropertyConfigurator?: boolean;
    onLeadBtnClick?: (property: IPropertyListProperty) => void;
    index?: number;
}

type IPropertyListApplicationButtonProperty = IPropertyListProperty | IPropertyConfiguratorProperty;

export const PropertyListApplicationButtonVariantBCD = (props: IProps) => {
    const {applicationSourceSection, property, vendorId, isPlansList, isInPropertyConfigurator, index} = props;
    const {isFullOfferFormVariant} = useLeadFormSimplifiedAbTest();

    const viewType = useAppSelector((state) => state.viewType);

    const applicationSourceSectionData = applicationSourceSection || ApplicationSourceSection.BODY;

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const forceShowPrice = shouldShowPrice && !!property.price;
    const propertyBadgeType = getPropertyBadgeType(property);
    const isSaleOverLimit = getOfferDisplayStatus(property.offer) === "limited";

    const clickText = getLeadButtonText({
        isPriceHidden: !shouldShowPrice,
        isProperty: true,
        propertyPrice: property.price,
        priceType: property.price_type ?? property.offer.price_type,
        preferShortFormat: !isPlansList,
        isFullOfferFormVariant
    });

    let source = "";
    switch (viewType.current) {
        case ViewType.OFFER: {
            source = !isPlansList
                ? `${GTMEventApplicationSourceSection.PROPERTY_LIST_PRICE}${index ? `_${index + 1}` : ""}`
                : GTMEventApplicationSourceSection.TILE;
            break;
        }
        case ViewType.OFFER_LIST_MAP: {
            source = !isPlansList
                ? `${GTMEventApplicationSourceSection.PROPERTY_LIST_PRICE_MAP}${index ? `_${index + 1}` : ""}`
                : GTMEventApplicationSourceSection.TILE_LISTING_MAP;
            break;
        }
        case ViewType.PROPERTY: {
            source = GTMEventApplicationSourceSection.TILE_OTHERS;
            break;
        }
        default: {
            // We do not want to send empty events. It's difficult to identify the source for other actions.
            // In my opinion, this logic shouldn't be here - the GTM source should be passed from the calling context.
            source = `${GTMEventApplicationSourceSection.ADDITIONAL_PROPERTY_LIST_PRICE}${index ? `_${index + 1}` : ""}`;
        }
    }

    const gtmProps = {
        clickText,
        source,
        vendorName: props.property.offer.vendor.name ?? props.property.offer.vendor.id.toString(),
        offerName: props.property.offer.name ?? props.property.offer.id.toString()
    };

    const onOpen = () => {
        if (property.is_reserved && !isPlansList) {
            gtmEventPropertyListClick("rezerwacja");
        } else if (!property.is_promo) {
            gtmEventPropertyListClick("zapytaj o cene");
        }
    };

    return (
        <ApplicationModalButton
            source={getSource(property, isPlansList, isInPropertyConfigurator, forceShowPrice)}
            onOpen={onOpen}
            render={({openModal}) => {
                if ((propertyBadgeType === "promotion" || property.is_promo) && !isPlansList) {
                    return <PromotionButton onClick={() => openModal(gtmProps)} property={property} showPrice={forceShowPrice} />;
                }

                if (propertyBadgeType === "reservation" && !isPlansList) {
                    return <ReservedButton onClick={() => openModal(gtmProps)} />;
                }

                return (
                    <AskForPropertyButtonVariantBCD
                        onClick={() => {
                            openModal(gtmProps);
                            props.onLeadBtnClick?.(property);
                        }}
                        twoPercentCreditAvailable={property.credit_two_percent}
                        isPlansList={isPlansList}
                        isInPropertyConfigurator={isInPropertyConfigurator}
                        price={property.price}
                        priceType={property.price_type ?? property.offer.price_type}
                        showPrice={shouldShowPrice}
                        currency={getCurrency({currency: property.offer.currency})}
                    />
                );
            }}
            sourceSection={applicationSourceSectionData}
            vendorId={vendorId}
            offerId={property.offer.id}
            propertyId={property.id}
            forceClose={isSaleOverLimit}
        />
    );
};

//  Utils
function getSource(property: IPropertyListApplicationButtonProperty, isPlansList?: boolean, isInPropertyConfigurator?: boolean, forceShowPrice?: boolean) {
    if (forceShowPrice || (property.configuration.show_price && !!property.price)) {
        return ApplicationSource.Property;
    }

    if (isInPropertyConfigurator) {
        return ApplicationSource.PropertyConfigurator;
    }

    if (property.is_promo && !isPlansList) {
        return ApplicationSource.Promotion;
    }

    if (property.is_reserved && !isPlansList) {
        return ApplicationSource.Reservation;
    }

    return ApplicationSource.PriceInquiry;
}
import {ApplicationModalButton} from "../../../application/components/ApplicationModalButton";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {useShouldShowPrice} from "../../../hooks/use_should_show_price";
import {useLeadFormSimplifiedAbTest} from "../../../lead/hooks/use_lead_form_simplified_ab_test";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {getLeadButtonText} from "../../../lead/utils/get_lead_button_text";
import {getOfferDisplayStatus} from "../../../offer/utils/get_offer_display_status";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {getPropertyBadgeType} from "../../../property/utils/get_property_badge_type";
import {IPropertyConfiguratorProperty} from "../../../property_configurator/types/IPropertyConfiguratorProperty";
import {useAppSelector} from "../../../store/hooks";
import {gtmEventPropertyListClick} from "../../../tracking/google_tag_manager/gtm_event_property_list";
import {getCurrency} from "../../../utils/get_currency";
import {ViewType} from "../../../view_type/ViewType";
import {AskForPropertyButtonVariantBCD} from "./AskForPropertyButtonVariantBC";
import {PromotionButton} from "./PromotionButton";
import {ReservedButton} from "./ReservedButton";

interface IProps {
    property: IPropertyListApplicationButtonProperty;
    isAutoresponderEnabled: boolean;
    vendorId: number;
    applicationSourceSection?: ApplicationSourceSection;
    isPlansList?: boolean;
    className?: string;
    isInPropertyConfigurator?: boolean;
    onLeadBtnClick?: (property: IPropertyListProperty) => void;
    index?: number;
}

type IPropertyListApplicationButtonProperty = IPropertyListProperty | IPropertyConfiguratorProperty;

export const PropertyListApplicationButtonVariantBCD = (props: IProps) => {
    const {applicationSourceSection, property, vendorId, isPlansList, isInPropertyConfigurator, index} = props;
    const {isFullOfferFormVariant} = useLeadFormSimplifiedAbTest();

    const viewType = useAppSelector((state) => state.viewType);

    const applicationSourceSectionData = applicationSourceSection || ApplicationSourceSection.BODY;

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const forceShowPrice = shouldShowPrice && !!property.price;
    const propertyBadgeType = getPropertyBadgeType(property);
    const isSaleOverLimit = getOfferDisplayStatus(property.offer) === "limited";

    const clickText = getLeadButtonText({
        isPriceHidden: !shouldShowPrice,
        isProperty: true,
        propertyPrice: property.price,
        priceType: property.price_type ?? property.offer.price_type,
        preferShortFormat: !isPlansList,
        isFullOfferFormVariant
    });

    let source = "";
    switch (viewType.current) {
        case ViewType.OFFER: {
            source = !isPlansList
                ? `${GTMEventApplicationSourceSection.PROPERTY_LIST_PRICE}${index ? `_${index + 1}` : ""}`
                : GTMEventApplicationSourceSection.TILE;
            break;
        }
        case ViewType.OFFER_LIST_MAP: {
            source = !isPlansList
                ? `${GTMEventApplicationSourceSection.PROPERTY_LIST_PRICE_MAP}${index ? `_${index + 1}` : ""}`
                : GTMEventApplicationSourceSection.TILE_LISTING_MAP;
            break;
        }
        case ViewType.PROPERTY: {
            source = GTMEventApplicationSourceSection.TILE_OTHERS;
            break;
        }
        default: {
            // We do not want to send empty events. It's difficult to identify the source for other actions.
            // In my opinion, this logic shouldn't be here - the GTM source should be passed from the calling context.
            source = `${GTMEventApplicationSourceSection.ADDITIONAL_PROPERTY_LIST_PRICE}${index ? `_${index + 1}` : ""}`;
        }
    }

    const gtmProps = {
        clickText,
        source,
        vendorName: props.property.offer.vendor.name ?? props.property.offer.vendor.id.toString(),
        offerName: props.property.offer.name ?? props.property.offer.id.toString()
    };

    const onOpen = () => {
        if (property.is_reserved && !isPlansList) {
            gtmEventPropertyListClick("rezerwacja");
        } else if (!property.is_promo) {
            gtmEventPropertyListClick("zapytaj o cene");
        }
    };

    return (
        <ApplicationModalButton
            source={getSource(property, isPlansList, isInPropertyConfigurator, forceShowPrice)}
            onOpen={onOpen}
            render={({openModal}) => {
                if ((propertyBadgeType === "promotion" || property.is_promo) && !isPlansList) {
                    return <PromotionButton onClick={() => openModal(gtmProps)} property={property} showPrice={forceShowPrice} />;
                }

                if (propertyBadgeType === "reservation" && !isPlansList) {
                    return <ReservedButton onClick={() => openModal(gtmProps)} />;
                }

                return (
                    <AskForPropertyButtonVariantBCD
                        onClick={() => {
                            openModal(gtmProps);
                            props.onLeadBtnClick?.(property);
                        }}
                        twoPercentCreditAvailable={property.credit_two_percent}
                        isPlansList={isPlansList}
                        isInPropertyConfigurator={isInPropertyConfigurator}
                        price={property.price}
                        priceType={property.price_type ?? property.offer.price_type}
                        showPrice={shouldShowPrice}
                        currency={getCurrency({currency: property.offer.currency})}
                    />
                );
            }}
            sourceSection={applicationSourceSectionData}
            vendorId={vendorId}
            offerId={property.offer.id}
            propertyId={property.id}
            forceClose={isSaleOverLimit}
        />
    );
};

//  Utils
function getSource(property: IPropertyListApplicationButtonProperty, isPlansList?: boolean, isInPropertyConfigurator?: boolean, forceShowPrice?: boolean) {
    if (forceShowPrice || (property.configuration.show_price && !!property.price)) {
        return ApplicationSource.Property;
    }

    if (isInPropertyConfigurator) {
        return ApplicationSource.PropertyConfigurator;
    }

    if (property.is_promo && !isPlansList) {
        return ApplicationSource.Promotion;
    }

    if (property.is_reserved && !isPlansList) {
        return ApplicationSource.Reservation;
    }

    return ApplicationSource.PriceInquiry;
}
